import { isValid } from "date-fns";
import React, { useState } from "react";

import { faPlus } from "@fortawesome/pro-regular-svg-icons";

//components
import { ButtonStandard, Chip, Text } from "@performyard/py-components";

import SelectInput, { Result } from "../../../Inputs/SelectInput";
import { Option, RadioButtons } from "../../../Inputs/SelectionOptions/RadioButtons";
import Spacer from "../../../Spacer";
//css
import css from "../../QueryBuilder.module.scss";
// utils
import { IQuery, IQueryField, getDefaultComparator, getSelectionFieldsAsResults } from "../../QueryBuilderUtils";
import { DATE_TYPES } from "../../constants";
import QueryComparator from "../QueryComparator";
import QueryValueInput from "../QueryValueInput";

const DateSubType = {
	static: { value: "static", label: "Static" },
	relative: { value: "relative", label: "Relative" },
	anniversary: { value: "anniversary", label: "Anniversary" },
};

interface SingleQueryInputProps {
	isLastQuery?: boolean;
	selections?: IQueryField[];
	onChange: (newQueryValue: any) => void;
	query: IQuery;
	addOrCondition: () => void;
	removeCondition: () => void;
	disableEmployeeFieldSelector?: boolean;
	uniqueKey?: string;
	isEdit?: boolean;
	enabledAnniversary?: boolean;
	fieldSelectPlaceholder?: string;
}

const getDateSubTypeIfDateTypeFromQuery = (query: IQuery) => {
	let dateSubType = DateSubType.static;
	// if the query is a date type and the query value is not a valid date, then it is a relative date
	if (
		query?.queryValue &&
		query?.field?.type &&
		DATE_TYPES.includes(query.field?.type) &&
		!isValid(query.queryValue.value)
	) {
		if (query.comparator.value.includes("anniversary")) {
			dateSubType = DateSubType.anniversary;
		} else {
			dateSubType = DateSubType.relative;
		}
	}

	return dateSubType;
};

/**
 * The SingleQueryInput component is used to build a single query in the QueryBuilder
 */
const SingleQueryInput = ({
	isLastQuery = true,
	selections = [],
	query,
	onChange,
	addOrCondition,
	removeCondition,
	disableEmployeeFieldSelector = false,
	isEdit = false,
	enabledAnniversary = false,
	fieldSelectPlaceholder = "Custom Field Name",
}: SingleQueryInputProps) => {
	const [currentDateSubType, setCurrentDateSubType] = useState<Option>(getDateSubTypeIfDateTypeFromQuery(query));
	const selectionFieldsAsResults = getSelectionFieldsAsResults(selections);
	const currentSelectedType = query?.field?.type;
	const aFieldIsSelected = !!currentSelectedType;
	const fieldIsDateType = DATE_TYPES.includes(currentSelectedType || "");

	const DateTypeDefaultOptions = [DateSubType.static, DateSubType.relative];

	const DateTypeOptions = enabledAnniversary
		? [...DateTypeDefaultOptions, DateSubType.anniversary]
		: DateTypeDefaultOptions;

	const handleOnChange = (newQueryValue: any) => {
		onChange(newQueryValue);
	};

	return (
		<div data-testid="singleQueryInput">
			<div className={css.header}>
				<Text size="paragraph-2" weight="semi-bold" color="neutral-9">
					{isEdit ? "Edit Condition" : "Add Condition"}
				</Text>
				<div>
					<ButtonStandard size="small" variant="micro" color="red" onClick={removeCondition}>
						<Text size="caption" weight="semi-bold">
							Clear
						</Text>
					</ButtonStandard>
				</div>
			</div>
			<Spacer height={2} />
			<div className={css.body}>
				<SelectInput
					// is it okay to leave this blank if it doesnt exist? or should we allow null to be an acceptable type for SelectInput > value?
					value={query.field?._id ? ({ value: query.field?._id, label: query.field?.name } as Result) : ""}
					onChange={(selectedValue) => {
						const selected = selections.find(
							(selection: IQueryField) => selection._id === selectedValue.value
						);
						const type = selected?.type || ""; // Add null check and provide default value
						const comparator = getDefaultComparator(type, currentDateSubType === DateSubType.static);
						handleOnChange({ field: selected, comparator, queryValue: "" });
						setCurrentDateSubType(DateSubType.static);
					}}
					results={selectionFieldsAsResults}
					disabled={disableEmployeeFieldSelector}
					placeholder={fieldSelectPlaceholder}
				/>
				<Spacer height={0.5} />
				{aFieldIsSelected ? (
					<div>
						{fieldIsDateType ? (
							<div className={css.dateSubType}>
								<Text size="paragraph-3" weight="semi-bold">
									Date Type
								</Text>
								<Spacer height={1} />
								<RadioButtons
									className={css.radioButtons}
									options={DateTypeOptions}
									value={currentDateSubType}
									onChange={(value) => {
										const comparator = getDefaultComparator(
											currentSelectedType || "",
											value === DateSubType.static,
											value === DateSubType.anniversary
										);
										handleOnChange({
											...query,
											comparator,
											queryValue: { label: "", value: "" },
										});
										setCurrentDateSubType(value);
									}}
									direction="row"
								/>
								<Spacer height={2} />
							</div>
						) : null}
						<QueryComparator
							comparator={query?.comparator}
							onChange={(comparator) => {
								handleOnChange({ ...query, comparator });
							}}
							fieldType={fieldIsDateType ? currentDateSubType.value : currentSelectedType}
						/>
						<Spacer height={0.5} />
						<QueryValueInput
							fieldType={fieldIsDateType ? currentDateSubType.value : currentSelectedType}
							label={query.comparator.label}
							value={query?.queryValue}
							onChange={(queryValue) => handleOnChange({ ...query, queryValue })}
							options={
								//find the selected field in the selections array and use the options from that field
								selections
									.find((selection) => selection._id === query.field?._id)
									?.options?.map((option) => ({ value: option, label: option })) || []
							}
						/>
					</div>
				) : null}
			</div>
			<Spacer height={2} />
			<div>
				{currentSelectedType &&
					(isLastQuery ? (
						<div className="w-fit">
							<ButtonStandard
								color="green"
								size="small"
								onClick={() => addOrCondition()}
								dangerouslySetClassName={"rounded-[16px]"}
								iconLeft={faPlus}
								disabled={query.queryValue?.value == null}
							>
								<div className="mr-1">
									<Text size="caption" weight="semi-bold" data-testid="or-button-text">
										OR
									</Text>
								</div>
							</ButtonStandard>
						</div>
					) : (
						<Chip label="OR" color="neutral" size={24} dangerouslySetClassName="w-6" />
					))}
			</div>
		</div>
	);
};

export default SingleQueryInput;
